import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  deleteDoc,
  limit,
} from 'firebase/firestore';
import { db } from './firebase';
import { OperationGroup, OperationGroupFormData, toOperationGroup } from '../models/OperationGroup';

const operationGroupsCollectionRef = collection(db, 'operationGroups');
const storesCollectionRef = collection(db, 'stores');

const normalizeGroupCode = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const buildOperationGroupId = (data: OperationGroupFormData): string => {
  const rawCode = data.code?.trim() || data.name.trim();
  const normalized = normalizeGroupCode(rawCode);
  if (!normalized) {
    throw new Error('Code groupe invalide');
  }
  return normalized;
};

export const operationGroupService = {
  getAllGroups: async (): Promise<OperationGroup[]> => {
    const querySnapshot = await getDocs(query(operationGroupsCollectionRef, orderBy('name')));
    return querySnapshot.docs.map((groupDoc) => toOperationGroup(groupDoc));
  },

  getActiveGroups: async (): Promise<OperationGroup[]> => {
    const querySnapshot = await getDocs(
      query(operationGroupsCollectionRef, where('isActive', '==', true), orderBy('name'))
    );
    return querySnapshot.docs.map((groupDoc) => toOperationGroup(groupDoc));
  },

  getGroupById: async (id: string): Promise<OperationGroup | null> => {
    const groupDoc = await getDoc(doc(db, 'operationGroups', id));
    return groupDoc.exists() ? toOperationGroup(groupDoc) : null;
  },

  createGroup: async (groupData: OperationGroupFormData): Promise<OperationGroup> => {
    const groupId = buildOperationGroupId(groupData);
    const groupRef = doc(db, 'operationGroups', groupId);
    const existing = await getDoc(groupRef);

    if (existing.exists()) {
      throw new Error(`Le groupe "${groupId}" existe déjà`);
    }

    await setDoc(groupRef, {
      name: groupData.name.trim(),
      city: groupData.city?.trim() || '',
      description: groupData.description?.trim() || '',
      isActive: groupData.isActive,
      deliveryResponsibleStoreId: null,
      storeIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    const created = await getDoc(groupRef);
    return toOperationGroup(created);
  },

  updateGroup: async (id: string, groupData: OperationGroupFormData): Promise<void> => {
    const groupRef = doc(db, 'operationGroups', id);
    const groupDoc = await getDoc(groupRef);
    if (!groupDoc.exists()) {
      throw new Error(`Le groupe "${id}" est introuvable`);
    }

    await updateDoc(groupRef, {
      name: groupData.name.trim(),
      city: groupData.city?.trim() || '',
      description: groupData.description?.trim() || '',
      isActive: groupData.isActive,
      updatedAt: serverTimestamp(),
    });
  },

  deleteGroup: async (id: string): Promise<void> => {
    const attachedStores = await getDocs(
      query(storesCollectionRef, where('operationGroupId', '==', id), limit(1))
    );

    if (!attachedStores.empty) {
      throw new Error('Impossible de supprimer un groupe qui contient encore des magasins');
    }

    await deleteDoc(doc(db, 'operationGroups', id));
  },
};

export const operationGroupUtils = {
  normalizeGroupCode,
};
